import { useEffect, useRef, useState } from "react";
import { X, CameraOff } from "lucide-react";

const TOKEN_RE = /\/o\/([A-Za-z0-9_-]{6,})/;
export const tokenFromScan = (text) => {
  const t = String(text || "").trim();
  const m = t.match(TOKEN_RE);
  if (m) return m[1];
  return /^[A-Za-z0-9_-]{6,}$/.test(t) ? t : null;
};

const errorText = (err) => {
  const n = err && err.name;
  if (n === "NotAllowedError" || n === "SecurityError")
    return "Kameraet er blokeret. Giv JaTak Pro adgang til kameraet i browserens indstillinger og prøv igen.";
  if (n === "NotFoundError" || n === "OverconstrainedError" || n === "NoCamera")
    return "Vi kunne ikke finde et kamera på denne enhed.";
  if (n === "NotReadableError") return "Kameraet bruges af en anden app. Luk den og prøv igen.";
  return "Kameraet kunne ikke startes. Prøv igen, eller indtast koden i stedet.";
};

// Rear camera QR scanner: BarcodeDetector fast path, @zxing/browser fallback. Stops the camera on close.
export default function QrScanner({ onResult, onClose, shopName }) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");
  const doneRef = useRef(false);

  useEffect(() => {
    let stream = null;
    let raf = 0;
    let zxingControls = null;
    let cancelled = false;
    const hit = (text) => {
      if (doneRef.current || !text) return;
      doneRef.current = true;
      onResult(text);
    };
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw Object.assign(new Error("no camera"), { name: "NoCamera" });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (cancelled) return;
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play().catch(() => {});
        let detector = null;
        if ("BarcodeDetector" in window) {
          try {
            const formats = await window.BarcodeDetector.getSupportedFormats();
            if (formats.includes("qr_code")) detector = new window.BarcodeDetector({ formats: ["qr_code"] });
          } catch {}
        }
        if (detector) {
          const loop = async () => {
            if (cancelled || doneRef.current) return;
            try {
              if (video.readyState >= 2) {
                const codes = await detector.detect(video);
                if (codes[0]) return hit(codes[0].rawValue);
              }
            } catch {}
            raf = requestAnimationFrame(loop);
          };
          loop();
        } else {
          const { BrowserQRCodeReader } = await import("@zxing/browser");
          if (cancelled) return;
          const reader = new BrowserQRCodeReader();
          zxingControls = await reader.decodeFromStream(stream, video, (res) => res && hit(res.getText()));
        }
      } catch (err) {
        if (!cancelled) setError(errorText(err));
      }
    })();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      zxingControls?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onResult]);

  return (
    <>
      <div className="sheet-scrim" onClick={onClose} data-testid="scanner-scrim" />
      <div className="sheet scanner" role="dialog" aria-label="Scan QR-kode" data-testid="qr-scanner">
        <div className="scanner-head">
          <div className="sh-title">
            <h2>Scan kundens QR-kode</h2>
            {shopName && <small className="sh-shop" title={shopName} data-testid="scanner-shop">Butik: <b>{shopName}</b></small>}
          </div>
          <button className="ib-close" aria-label="Luk" onClick={onClose} data-testid="scanner-close">
            <X size={20} />
          </button>
        </div>
        {error ? (
          <div className="scanner-error" role="alert" data-testid="scanner-error">
            <CameraOff size={32} />
            <p>{error}</p>
          </div>
        ) : (
          <div className="scanner-view">
            <video ref={videoRef} playsInline muted autoPlay data-testid="scanner-video" />
            <span className="scanner-frame" aria-hidden="true" />
          </div>
        )}
        <p className="muted scanner-hint">Hold QR-koden fra kundens ordreside inden for rammen.</p>
      </div>
    </>
  );
}
